// ============================================================
// FEATURE: CRT Terminal — green-phosphor DOS/CRT manifesto shell
// Non-draggable content component. Mount as the artComp of a
// WindowsCard so it inherits the draggable titlebar + minimize.
//
// AMBULATE.EXE is also a live POWER CONSOLE: its commands reach out
// and re-paint the whole site by driving the <html> root element
// (document.documentElement) — a composed CSS filter string, a set
// of CSS custom properties, and a dedicated `crt-env` root class.
// This is a deliberately SEPARATE layer from the FloorToolbar
// (which toggles classes on <body> and child nodes), so the two
// never permanently overwrite each other. `reset` wipes the entire
// terminal layer, and unmount cleans it up too.
// ============================================================

import * as React from "react";
import { useState, useEffect, useRef } from "react";
import './crtterminal.css';

// Surreal ambulation/eye manifesto + poetry. Menacing-text motif.
const MANIFESTO = [
    "AMBULATE.EXE // a found-object ideaboard that walks at night.",
    "we do not browse. we ambulate. one foot, then the wet other.",
    "every pixel here is a small eye that learned to blink in dot-matrix.",
    "the gradient wall breathes pink, lime, periwinkle, salmon, then forgets.",
    "press any tile and it powers on, hue-rotated, saturated, screaming softly.",
    "you are being watched by the artwork. it is polite about it. mostly.",
    "to ambulate is to refuse stillness. the cursor agrees and never stops blinking.",
    "this shell is wired to the walls. type help — every command re-paints the room.",
];

// Random surreal lines for the `ambulate` command.
const AMBU_LINES = [
    "the eyes in the brick have started taking the same route home as you.",
    "a thousand legs, one corridor, no destination, this is the gallery.",
    "walk slower. the phosphor is trying to memorize your shape.",
    "the receipt printer prophesied your arrival in tight green glow.",
    "somewhere a bolt loosens by exactly a random number of degrees.",
    "you left a footprint on the scanline. it will heal by morning.",
];

const FILES = [
    "manifesto.txt   eyes.dat       brick.png",
    "ambulation/     receipts/      bolts/",
    "menacing.eye    coin.gif       letter.tng",
];

// ASCII inked-eye, echoing the TheEye sketch motif in phosphor.
const EYE_ART = [
    "   .-=========-.",
    "  /   .-===-.   \\",
    " |   (  (o)  )   |",
    "  \\   '-===-'   /",
    "   '-=========-'",
    "      it sees.",
];

const BOOT = [
    "AMBULATE-DOS  v1.97  [phosphor build]",
    "(c) 1997-2026 ambulation zone. all eyes reserved.",
    "640K conventional memory, 4 stops of rainbow ok",
    "ENV power console online. the walls are listening.",
    "",
    "type help and walk.",
    "",
];

// ------------------------------------------------------------
// ENVIRONMENT ENGINE
// All site-wide visual state the terminal can drive lives in one
// plain object. We recompose a single CSS filter string from it and
// stamp it (+ a few CSS vars + a root class) onto <html>. Nothing
// here mutates React state of other components — it is pure DOM, so
// it composes with, rather than clobbers, the FloorToolbar layer.
// ------------------------------------------------------------

const ROOT_CLASS = "crt-env";

// Named colors the `color` command understands (besides any #hex / css name).
const COLOR_NAMES = {
    lime: "#bbff8e",
    pink: "#ffd8d8",
    salmon: "#ff8e8e",
    periwinkle: "#8eacff",
    phosphor: "#12fe8d",
    amber: "#ffb000",
    blood: "#d81e5b",
    void: "#0a0a0a",
    bone: "#f4ecd8",
};

// The default / cleared environment.
function freshEnv() {
    return {
        hue: 0,          // deg
        invert: false,
        sepia: 0,        // 0..1
        grayscale: 0,    // 0..1
        brightness: 1,   // multiplier
        contrast: 1,     // multiplier
        saturate: 1,     // multiplier
        blur: 0,         // px
        accent: null,    // hex string or null
        grid: false,     // graph-paper overlay
        scanlines: false,// CRT scanline overlay
    };
}

// Compose the active modifiers into one CSS filter string.
function composeFilter(env) {
    const parts = [];
    if (env.hue) parts.push(`hue-rotate(${env.hue}deg)`);
    if (env.invert) parts.push("invert(1)");
    if (env.sepia) parts.push(`sepia(${env.sepia})`);
    if (env.grayscale) parts.push(`grayscale(${env.grayscale})`);
    if (env.brightness !== 1) parts.push(`brightness(${env.brightness})`);
    if (env.contrast !== 1) parts.push(`contrast(${env.contrast})`);
    if (env.saturate !== 1) parts.push(`saturate(${env.saturate})`);
    if (env.blur) parts.push(`blur(${env.blur}px)`);
    return parts.join(" ");
}

// Push the env object out onto the live <html> element.
function applyEnv(env) {
    const root = document.documentElement;
    if (!root) return;

    const filter = composeFilter(env);
    // Our own filter property — distinct from body's .invert/.blur classes.
    root.style.filter = filter;
    // -webkit- for older Safari that ignores plain filter on <html>.
    root.style.webkitFilter = filter;

    // Accent tint exposed as a CSS var + a faint full-page wash.
    if (env.accent) {
        root.style.setProperty("--crt-accent", env.accent);
    } else {
        root.style.removeProperty("--crt-accent");
    }

    // Toggle root class so CSS (overlays, tint wash) can react.
    root.classList.toggle(ROOT_CLASS, isEnvActive(env));
    root.classList.toggle("crt-env-grid", env.grid);
    root.classList.toggle("crt-env-scan", env.scanlines);
    root.classList.toggle("crt-env-accent", !!env.accent);
}

// Is anything non-default set?
function isEnvActive(env) {
    const f = freshEnv();
    return Object.keys(f).some((k) => env[k] !== f[k]);
}

// Fully strip every terminal-applied document-level change.
function clearEnv() {
    const root = document.documentElement;
    if (!root) return;
    root.style.filter = "";
    root.style.webkitFilter = "";
    root.style.removeProperty("--crt-accent");
    root.classList.remove(ROOT_CLASS, "crt-env-grid", "crt-env-scan", "crt-env-accent", "crt-env-glitch");
}

// Validate + normalize a color token to a hex/css color or null.
function resolveColor(token) {
    if (!token) return null;
    const t = token.trim().toLowerCase();
    if (COLOR_NAMES[t]) return COLOR_NAMES[t];
    if (/^#?[0-9a-f]{3}$/.test(t) || /^#?[0-9a-f]{6}$/.test(t)) {
        return t[0] === "#" ? t : "#" + t;
    }
    // Fall back to CSS-name validation via the browser.
    const probe = new Option().style;
    probe.color = "";
    probe.color = t;
    return probe.color ? t : null;
}

// Parse a numeric arg, returning null if invalid.
function num(token) {
    if (token == null || token === "") return null;
    const n = Number(token);
    return Number.isFinite(n) ? n : null;
}

// on/off/toggle helper for boolean commands.
function onOff(token, current) {
    if (token === "on" || token === "1" || token === "true") return true;
    if (token === "off" || token === "0" || token === "false") return false;
    return !current; // bare command toggles
}

function CrtTerminal(props) {
    const [lines, setLines] = useState(BOOT);
    const [input, setInput] = useState("");
    const [isTyping, setIsTyping] = useState(true);
    // tiny tick state used purely to re-render the growing live typing tail
    const [liveTick, setLiveTick] = useState(0);

    const screenRef = useRef(null);
    const timerRef = useRef(null);
    // typewriter progress trackers (kept in refs so the interval is stable)
    const stanzaRef = useRef(0);
    const charRef = useRef(0);
    const currentRef = useRef("");

    // Live environment state. Held in a ref (DOM is the source of truth,
    // not React render) so command handlers can read+mutate synchronously.
    const envRef = useRef(freshEnv());
    // pending glitch-burst timeout so we can clear it on unmount
    const glitchTimerRef = useRef(null);

    // --- TYPEWRITER: reveal the manifesto char-by-char into a live tail line,
    //     committing each finished stanza into the lines log. ---
    useEffect(() => {
        timerRef.current = setInterval(() => {
            // guard: a late tick must not run after we've already finished/cleared
            if (timerRef.current == null) return;

            const stanzaIdx = stanzaRef.current;

            if (stanzaIdx >= MANIFESTO.length) {
                clearInterval(timerRef.current);
                timerRef.current = null;
                setIsTyping(false);
                return;
            }

            const stanza = MANIFESTO[stanzaIdx];
            const charIdx = charRef.current;

            if (charIdx < stanza.length) {
                // grow the live tail line; tick to force a re-render of it
                currentRef.current = stanza.slice(0, charIdx + 1);
                charRef.current = charIdx + 1;
                setLiveTick((t) => t + 1);
            } else {
                // commit finished stanza into the permanent log
                const committed = currentRef.current;
                currentRef.current = "";
                charRef.current = 0;
                stanzaRef.current = stanzaIdx + 1;
                setLines((prev) => [...prev, committed]);
                setLiveTick((t) => t + 1);
            }
        }, 28);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // --- CLEANUP: on unmount, strip every document-level change the
    //     terminal made so the site returns to normal. ---
    useEffect(() => {
        return () => {
            if (glitchTimerRef.current) {
                clearTimeout(glitchTimerRef.current);
                glitchTimerRef.current = null;
            }
            clearEnv();
        };
    }, []);

    // --- AUTO-SCROLL whenever the log (or live tail) changes ---
    useEffect(() => {
        const el = screenRef.current;
        if (el) el.scrollTop = el.scrollHeight;
    }, [lines, liveTick]);

    // Apply the current env ref to the DOM (call after any mutation).
    function flushEnv() {
        applyEnv(envRef.current);
    }

    // Fire a brief glitch burst, then auto-clear it.
    function glitchBurst() {
        const root = document.documentElement;
        if (!root) return ["> env.glitch — reality stutters"];
        root.classList.add("crt-env-glitch", ROOT_CLASS);
        if (glitchTimerRef.current) clearTimeout(glitchTimerRef.current);
        glitchTimerRef.current = setTimeout(() => {
            const r = document.documentElement;
            if (r) {
                r.classList.remove("crt-env-glitch");
                // drop the root class again only if nothing else is active
                if (!isEnvActive(envRef.current)) r.classList.remove(ROOT_CLASS);
            }
            glitchTimerRef.current = null;
        }, 700);
        return ["> env.glitch — reality stutters for a moment"];
    }

    // --- COMMAND PARSER ---
    function runCommand(raw) {
        const trimmed = raw.trim();
        const cmd = trimmed.toLowerCase();
        const echo = "C:\\AMBULATE> " + raw;

        // split into verb + first arg
        const tokens = cmd.split(/\s+/);
        const verb = tokens[0];
        const arg = tokens[1];
        const env = envRef.current;

        if (verb === "clear" || verb === "cls") {
            setLines([]);
            return;
        }

        let response;
        switch (verb) {
            case "":
                response = [];
                break;
            case "help":
                response = [
                    "available commands:",
                    "  help                 this list",
                    "  whoami               who is operating the eyes",
                    "  about                what this place is",
                    "  ls                   list the artifacts",
                    "  ambulate             emit one manifesto line",
                    "  eyes                 summon the watcher",
                    "  date                 query the phosphor clock",
                    "  clear                wipe the phosphor",
                    "  -- ENV POWER CONSOLE (repaints the whole site) --",
                    "  color <hex|name>     set a site-wide accent tint",
                    "  hue <deg>            hue-rotate the entire page",
                    "  invert [on|off]      flip every color",
                    "  sepia [n]            wash the page sepia (0..1)",
                    "  grayscale [n]        drain the color (0..1)",
                    "  brightness <n>       scale brightness (1=normal)",
                    "  contrast <n>         scale contrast (1=normal)",
                    "  saturate <n>         scale saturation (1=normal)",
                    "  blur <px>            soften the whole room",
                    "  crt                  green-phosphor whole-site preset",
                    "  noir                 high-contrast grayscale preset",
                    "  grid [on|off]        toggle the graph-paper grid",
                    "  scanlines [on|off]   toggle CRT scanline overlay",
                    "  glitch               brief glitch burst, auto-clears",
                    "  reset                clear ALL env changes",
                ];
                break;
            case "whoami":
                response = ["nathan breen, ambulator"];
                break;
            case "about":
                response = [
                    "ambulation zone: a maximalist skeuomorphic-arcade ideaboard.",
                    "surreal generative art, pinned, framed, and bolted to a grungy desktop.",
                ];
                break;
            case "ls":
            case "dir":
                response = FILES.slice();
                break;
            case "ambulate":
                response = [AMBU_LINES[Math.floor(Math.random() * AMBU_LINES.length)]];
                break;
            case "eyes":
                response = EYE_ART.slice();
                break;
            case "date":
                response = [new Date().toString().toLowerCase() + "  (phosphor time)"];
                break;

            // ---------- ENV POWER CONSOLE ----------
            case "color": {
                const resolved = resolveColor(arg);
                if (!resolved) {
                    response = ["err: '" + (arg || "") + "' is not a color the walls accept. try a #hex or name (lime, amber, blood...)."];
                    break;
                }
                env.accent = resolved;
                flushEnv();
                response = ["> env.color = " + resolved + "  (site tint armed)"];
                break;
            }
            case "hue": {
                const d = num(arg);
                if (d === null) { response = ["err: hue needs degrees, e.g. hue 120"]; break; }
                env.hue = ((d % 360) + 360) % 360;
                flushEnv();
                response = ["> env.hue = " + env.hue + "deg"];
                break;
            }
            case "invert": {
                env.invert = onOff(arg, env.invert);
                flushEnv();
                response = ["> env.invert = " + (env.invert ? "on" : "off")];
                break;
            }
            case "sepia": {
                const v = arg == null ? 1 : num(arg);
                if (v === null) { response = ["err: sepia takes 0..1, e.g. sepia 0.6"]; break; }
                env.sepia = Math.max(0, Math.min(1, v));
                flushEnv();
                response = ["> env.sepia = " + env.sepia];
                break;
            }
            case "grayscale":
            case "greyscale": {
                const v = arg == null ? 1 : num(arg);
                if (v === null) { response = ["err: grayscale takes 0..1, e.g. grayscale 1"]; break; }
                env.grayscale = Math.max(0, Math.min(1, v));
                flushEnv();
                response = ["> env.grayscale = " + env.grayscale];
                break;
            }
            case "brightness": {
                const v = num(arg);
                if (v === null || v < 0) { response = ["err: brightness needs a number >= 0, e.g. brightness 1.4"]; break; }
                env.brightness = v;
                flushEnv();
                response = ["> env.brightness = " + v];
                break;
            }
            case "contrast": {
                const v = num(arg);
                if (v === null || v < 0) { response = ["err: contrast needs a number >= 0, e.g. contrast 1.5"]; break; }
                env.contrast = v;
                flushEnv();
                response = ["> env.contrast = " + v];
                break;
            }
            case "saturate": {
                const v = num(arg);
                if (v === null || v < 0) { response = ["err: saturate needs a number >= 0, e.g. saturate 2"]; break; }
                env.saturate = v;
                flushEnv();
                response = ["> env.saturate = " + v];
                break;
            }
            case "blur": {
                const v = num(arg);
                if (v === null || v < 0) { response = ["err: blur needs px >= 0, e.g. blur 3"]; break; }
                env.blur = v;
                flushEnv();
                response = ["> env.blur = " + v + "px"];
                break;
            }
            case "crt": {
                // green-phosphor whole-site preset
                env.hue = 95;
                env.saturate = 1.8;
                env.contrast = 1.25;
                env.brightness = 1.05;
                env.scanlines = true;
                env.accent = COLOR_NAMES.phosphor;
                flushEnv();
                response = ["> env preset CRT engaged — the whole room goes phosphor."];
                break;
            }
            case "noir": {
                // high-contrast grayscale preset
                env.grayscale = 1;
                env.contrast = 1.5;
                env.brightness = 0.9;
                env.accent = null;
                env.scanlines = false;
                flushEnv();
                response = ["> env preset NOIR engaged — color drains from the walls."];
                break;
            }
            case "grid": {
                env.grid = onOff(arg, env.grid);
                flushEnv();
                response = ["> env.grid = " + (env.grid ? "on" : "off")];
                break;
            }
            case "scanlines":
            case "scanline": {
                env.scanlines = onOff(arg, env.scanlines);
                flushEnv();
                response = ["> env.scanlines = " + (env.scanlines ? "on" : "off")];
                break;
            }
            case "glitch": {
                response = glitchBurst();
                break;
            }
            case "reset": {
                envRef.current = freshEnv();
                if (glitchTimerRef.current) {
                    clearTimeout(glitchTimerRef.current);
                    glitchTimerRef.current = null;
                }
                clearEnv();
                response = ["> env reset — the walls exhale. normal restored."];
                break;
            }

            default:
                response = ["'" + trimmed + "' is not recognized. the eyes do not blink for it."];
                break;
        }

        setLines((prev) => [...prev, echo, ...response]);
    }

    function handleKeyDown(e) {
        if (e.key === "Enter") {
            e.preventDefault();
            runCommand(input);
            setInput("");
        }
    }

    // live typing tail (only while typing and mid-stanza)
    const liveTail = isTyping && currentRef.current ? currentRef.current : null;

    function focusInput() {
        const i = document.getElementById("crt-term-input");
        if (i) i.focus();
    }

    return (
        <div className="crt-screen" onClick={focusInput}>
            <div className="crt-scan" />
            <div className="crt-log" ref={screenRef}>
                {lines.map((ln, i) => (
                    <div className="crt-line" key={i}>{ln === "" ? " " : ln}</div>
                ))}
                {liveTail !== null && (
                    <div className="crt-line">
                        {liveTail}
                        <span className="crt-cursor">{"█"}</span>
                    </div>
                )}
                {!isTyping && (
                    <div className="crt-line crt-prompt">
                        <span className="crt-ps">{"C:\\AMBULATE>"}</span>
                        <span className="crt-input-wrap">
                            <input
                                id="crt-term-input"
                                className="crt-input"
                                type="text"
                                value={input}
                                spellCheck={false}
                                autoComplete="off"
                                autoCapitalize="off"
                                autoCorrect="off"
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={handleKeyDown}
                            />
                            <span className="crt-typed">{input}</span>
                            <span className="crt-cursor">{"█"}</span>
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CrtTerminal;
